import { Skeleton } from 'antd';
import { isMobile } from '@wajeez-web/components/helpers';
import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';
import { MediaHelpers } from '@wajeez-web/core/api';
import SVG from 'react-inlinesvg';

export const FeaturedContent = ({ component, context }: BaseComponentProps) => {
  const { contents, contentsRequestStatus } = useComponentData(
    component,
    context
  );

  const { isActive } = useSkeletonHelper(
    contentsRequestStatus,
    contents?.length ? true : false
  );

  return contentsRequestStatus !== 'loading' &&
    contentsRequestStatus !== 'failed' ? (
    <div className="w-full">
      <FeaturedContentItem
        key={contents?.[0]?.id}
        component={{ ...component?.itemTemplate, uniqueId: 0 }}
        context={contents?.[0]}
      />
    </div>
  ) : (
    <FeaturedContentSkeleton isActive={isActive} />
  );
};

const FeaturedContentSkeleton = ({
  isActive = true,
}: {
  isActive?: boolean;
}) => {
  return (
    <div className="w-full px-4 sm:px-0">
      <div className="w-full hidden sm:flex flex-col justify-start items-start">
        <Skeleton.Button
          active={isActive}
          shape="square"
          className="!w-[200px] mb-1 dark:bg-dark-medium"
        />
        <Skeleton.Node
          active={isActive}
          className="!w-[978px] !h-[178px] dark:bg-dark-medium"
        >
          <div />
        </Skeleton.Node>
      </div>
      <div className="w-full sm:hidden">
        <Skeleton.Node
          active={isActive}
          className="!w-full !h-[290px] dark:bg-dark-medium"
        >
          <div />
        </Skeleton.Node>
        <div className="w-full">
          <Skeleton.Button
            active={isActive}
            shape="square"
            className="mt-4 !w-[200px] !h-[30px] dark:bg-dark-medium"
          />
          <Skeleton.Button
            active={isActive}
            shape="square"
            className="mt-3 !w-full !h-[25px] dark:bg-dark-medium"
          />
          <Skeleton.Button
            active={isActive}
            shape="square"
            className="mt-2 !w-full !h-[25px] dark:bg-dark-medium"
          />
          <Skeleton.Button
            active={isActive}
            shape="square"
            className="mt-2 !w-full !h-[25px] dark:bg-dark-medium"
          />
        </div>
      </div>
    </div>
  );
};

export interface FeaturedContentItemParameters {
  image: string | null;
  backgroundImage: string | null;
  description: string | null;
}

const FeaturedContentItem = ({ component, context }: BaseComponentProps) => {
  const { getParameterValue, content } =
    useComponentData<FeaturedContentItemParameters>(component, context);

  return content ? (
    <>
      <h1 className="hidden sm:block text-[18px] font-bold text-[#27160E]">
        اخترنا لك اليوم
      </h1>
      <ClickableComponentLayout content={content} widthStyle="w-fit">
        <div className="w-full sm:w-[978px] px-4 sm:px-0 pt-4 sm:pt-0 sm:flex sm:justify-center sm:items-center">
          <div className="w-full sm:max-w-[544px] h-[290px] sm:h-[fit-content] sm:pt-[17px] sm:pb-[24px] sm:pr-[43px] rounded-t-[10px] sm:rounded-t-none sm:rounded-r-[4px] relative sm:bg-[#E9F3F3] sm:dark:bg-[#1C1C1E]">
            <div
              style={{
                backgroundImage:
                  'url(' +
                  MediaHelpers.getPublicMediaById(
                    getParameterValue('backgroundImage')
                  ) +
                  ')',
              }}
              className={`sm:hidden h-full w-full rounded-t-[10px] backdrop-blur-lg bg-cover bg-center bg-no-repeat`}
            ></div>
            <div className="absolute sm:hidden backdrop-blur-lg flex justify-center px-[62px] py-[35px] w-full h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <img
                src={MediaHelpers.getPublicMediaById(
                  getParameterValue('image')
                )}
                alt=""
                className="w-full h-full rounded-[10px] shadow-md"
              />
            </div>
            <div className="hidden sm:flex sm:justify-start sm:items-start">
              <div className="backdrop-blur-lg w-[130px] h-[130px]">
                <img
                  src={MediaHelpers.getPublicMediaById(
                    getParameterValue('image')
                  )}
                  alt=""
                  className="w-full h-full rounded-[10px] sm:rounded-[6px] shadow-md"
                />
              </div>
              <div className="mr-[11px] pt-[37px] text-[15px] text-[#545454]">
                <div className="font-bold">{context?.title}</div>
                <div className="font-medium">
                  {context?.authors?.join(' ,')}
                </div>
                <div className="mt-[15px] pr-[7px] flex justify-start items-center">
                  <SVG src={`/assets/icons/play.svg`} />
                  <div className="mr-[10px] text-black text-[15px] font-medium">
                    {context?.durationMS &&
                      Math.ceil(context?.durationMS / (1000 * 60))}{' '}
                    دقيقة
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sm:hidden bg-[#EDEDED] dark:bg-[#1C1C1E] px-4 pt-[14px] pb-5 flex flex-col space-y-4 rounded-b-[10px] text-[#161819] dark:text-white">
            <div className="border rounded-full border-[#292929] dark:border-[#DDDDDD] px-3 py-2 w-fit">
              إخترنا لك اليوم
            </div>
            <div className="font-bold text-[21px] leading-[29px]">
              {context?.title}
            </div>
            <div className="text-[16px] leading-[23px]">
              {getParameterValue('description')}
            </div>
          </div>
          <div className="hidden sm:flex basis-[434px] min-w-[220px]  w-full self-stretch bg-[#E9F3F3] dark:bg-[#1C1C1E] rounded-l-[4px] pl-[24px] sm:pl-[12px] lg:pl-[24px] py-5 sm:py-3 lg:py-5 justify-center items-center text-[#161819] dark:text-white">
            <div className="w-full h-full pr-[36px] sm:pr-[14px] lg:pr-[36px] border-r border-[#C3CDCF] flex justify-center items-center">
              <div className="text-[20px]  flex-wrap font-medium">
                {isMobile()
                  ? getParameterValue('description')
                  : getParameterValue('description')?.slice(0, 100)}
                {!isMobile() &&
                  (getParameterValue('description')?.length || 0) > 100 &&
                  '...'}
              </div>
            </div>
          </div>
        </div>
      </ClickableComponentLayout>
    </>
  ) : null;
};
